import { useState } from "react";
import SEO from "../components/SEO";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Calendar, Users, TrendingUp, Home, Heart, Shield, DollarSign, Clock, CheckCircle, ChevronRight, Menu, X, ChevronDown, Star, BarChart3, Crown } from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate, Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/jd65gb5i_CityWorth%20Logo.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/dg6nptih_13657703-27dd-4449-b034-06b0b933df77.png";
const HAPPY_EMPLOYEES_1 = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/8bvbouj3_9aa9ffbb736b703184bd002f745d4f337501b5bca1882349e2124d9dbe1a7b72.png";
const HAPPY_EMPLOYEES_2 = "https://images.unsplash.com/photo-1758691737138-7b9b1884b1db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGVtcGxveWVlcyUyMG9mZmljZSUyMHRlYW13b3JrJTIwc21pbGluZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzE4NzU0NDl8MA&ixlib=rb-4.1.0&q=85";
const HAPPY_FAMILY = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/jnz1mrxf_d40c9ee4d5fa50270b902b1eea68d32de3e48b4bb6565ac086b3e38791a4714a.png";

// ============================================
// ZOOM LINK - UPDATE THIS WHEN CHANGING WEBINAR
// This is the ONLY place the Zoom link is defined
// All "Register" buttons that go directly to Zoom use this
// ============================================
const ZOOM_WEBINAR_LINK = "https://zoom.us/webinar/register/WN__fC109gdSYC3mFsF8cBJJA";
// ============================================

const HomePage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setIsMenuOpen(false);
  };

  const menuItems = [
    { label: "Home", action: () => navigate("/") },
    { label: "How It Works", action: () => navigate("/how-it-works") },
    { label: "Retention Programs", action: () => navigate("/retention-programs") },
    { label: "Retention Analysis", action: () => navigate("/retention-analysis") },
    { label: "Employee Experience", action: () => navigate("/employee-experience") },
    { label: "Our Story", action: () => navigate("/our-story") },
    { label: "Retention Calculator", action: () => navigate("/retention-calculator") },
    { label: "Events", action: () => navigate("/events") },
    { label: "Book a Call", action: () => navigate("/book-a-call") },
    { label: "Careers", action: () => navigate("/careers") },
  ];

  const stats = [
    { value: "3x", label: "Longer tenure for homeowners vs renters" },
    { value: "#1", label: "Cause of employee stress: finances" },
    { value: "70%", label: "Higher life satisfaction for homeowners" },
  ];

  const processSteps = [
    { num: "1", title: "Education", desc: "Financial literacy and homebuying workshops" },
    { num: "2", title: "Financial Review", desc: "Personalized assessment and planning" },
    { num: "3", title: "Pre-Approval", desc: "Streamlined mortgage qualification" },
    { num: "4", title: "Home Search", desc: "Search, negotiation, and making an offer" },
    { num: "5", title: "Closing", desc: "Seamless transaction to ownership" },
  ];

  const financialBenefits = [
    { icon: DollarSign, title: "Builds Equity", desc: "Creates long-term wealth for employees" },
    { icon: Shield, title: "Reduces Rent Risk", desc: "Protection from rent inflation" },
    { icon: TrendingUp, title: "Community Investment", desc: "Increases personal stake in community" },
  ];

  const mentalBenefits = [
    { icon: Heart, title: "Sense of Control", desc: "Ownership over living situation" },
    { icon: Users, title: "Family Stability", desc: "Secure environment for families" },
    { icon: CheckCircle, title: "Future Confidence", desc: "Peace of mind and reduced stress" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-white"
      data-testid="home-page"
    >
      <SEO title="Employee Homeownership & Retention Solutions" description="Homeowners stay 3x longer than renters. CityWorth helps companies reduce turnover and build more stable teams through homeownership benefits." />
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-100 py-2">
        <div className="container-main flex items-center justify-between py-8 md:py-10">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-10 md:h-12" />
          </Link>
          
          <div className="flex items-center gap-4">
            {/* Get Started Button */}
            <Link to="/get-started">
              <Button className="btn-primary text-white px-6 py-2.5">Get Started</Button>
            </Link>
            
            {/* Dropdown Menu */}
            <div className="relative">
              <Button 
                variant="outline" 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center gap-2 border-slate-300 px-4 py-2.5"
              >
                <Menu className="w-5 h-5" />
                <span className="hidden sm:inline">Menu</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              <AnimatePresence>
                {isMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsMenuOpen(false)}
                    />
                    
                    {/* Dropdown */}
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50"
                    >
                      {menuItems.map((item, i) => (
                        <button
                          key={i}
                          onClick={item.action}
                          className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                        >
                          {item.label}
                        </button>
                      ))}
                      <div className="border-t border-slate-100 my-2" />
                      {/* ZOOM LINK BUTTON - Goes directly to Zoom registration */}
                      <a 
                        href={ZOOM_WEBINAR_LINK} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block px-4 py-3 text-red-600 hover:bg-red-50 font-medium transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Register for Webinar →
                      </a>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-12 pb-20 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-red-50/30" />
        <div className="absolute top-20 right-0 w-1/2 h-96 bg-gradient-to-l from-blue-50/50 to-transparent" />
        
        <div className="container-main relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 leading-tight mb-6">
                Employee Homeownership &{" "}
                <span className="gradient-text">Retention Solutions</span>
              </h1>
              <p className="text-xl text-slate-600 mb-4">
                Employees who own stay <strong>3x longer</strong> than employees who rent.
              </p>
              <p className="text-lg text-slate-500 mb-8">
                The future of retention is not another perk. It is stability.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {/* PRIMARY CTA - Goes to Events page */}
                <Link to="/events">
                  <Button size="lg" className="btn-primary text-white px-8 py-6 text-lg">
                    Register for The Big Shift Webinar
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/get-started">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="px-8 py-6 text-lg border-slate-300"
                  >
                    Learn More
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={HERO_IMAGE} 
                  alt="Happy family in front of their new home" 
                  className="w-full h-[420px] object-cover"
                />
              </div>
              <motion.div 
                className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-lg border border-slate-100 hidden lg:flex"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">3x</div>
                    <div className="text-sm text-slate-500">Longer Retention</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 bg-slate-50" data-testid="problem-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-4xl font-semibold text-slate-900 mb-8 max-w-3xl mx-auto leading-snug">
              Your Benefits Package Is{" "}
              <span className="gradient-text">Missing the One That Matters Most</span>
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                Here's the hard truth: No matter how many great benefits you already offer — competitive salaries, health insurance, 401k matching, flexible PTO, or wellness programs — if your employees are still renters, <strong className="text-slate-800">you haven't solved the biggest driver of turnover.</strong>
              </p>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                They remain mobile, financially unstable, and far more likely to leave at the first better opportunity. The renter status overrides everything else when it comes to <span className="gradient-text font-semibold">long-term loyalty</span>.
              </p>
            </div>
          </motion.div>

          {/* The Cycle vs The Fix — Side by Side */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {/* The Problem */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl border border-slate-200 p-8 md:p-10 relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-slate-300" />
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">The Cycle</span>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Hire. Train. Lose. Repeat.</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Most companies are trapped in the same expensive loop — pouring money into recruiting while culture and institutional knowledge walk out the door with every departure.
              </p>
              <div className="space-y-3">
                {[
                  { icon: DollarSign, text: "Average replacement cost: 50–200% of salary" },
                  { icon: Clock, text: "6–9 months to fully onboard a new hire" },
                  { icon: Users, text: "Team morale dips with every exit" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-slate-400" />
                    </div>
                    <span className="text-sm text-slate-600">{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* The Fix */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl border border-red-100 p-8 md:p-10 relative overflow-hidden shadow-sm"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-blue-600" />
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-red-500 mb-4">The Fix</span>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Root them. <span className="gradient-text">Retain them.</span></h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Companies using CityWorth aren't outspending anyone. They've simply unlocked a benefit that creates <strong>real roots</strong> — and their employees stay <strong>3x longer</strong> because of it.
              </p>
              <div className="space-y-3">
                {[
                  { icon: Home, text: "Homeownership = stability and community ties" },
                  { icon: TrendingUp, text: "3x longer tenure vs renters, across all industries" },
                  { icon: Heart, text: "70% higher life satisfaction for homeowners" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-red-600" />
                    </div>
                    <span className="text-sm text-slate-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Big Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-8 md:p-10 mb-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 text-center">
              {stats.map((stat, i) => (
                <div key={i} className={`${i !== 2 ? 'md:border-r md:border-slate-700' : ''}`}>
                  <div className="text-4xl md:text-5xl font-black gradient-text mb-2">{stat.value}</div>
                  <div className="text-slate-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Full-width Image + CTA */}
          <div className="relative rounded-2xl overflow-hidden shadow-xl">
            <img 
              src={HAPPY_EMPLOYEES_1} 
              alt="Happy employees working together" 
              className="w-full h-64 md:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/50 to-transparent flex items-center">
              <div className="p-8 md:p-12 max-w-xl">
                <p className="text-white text-xl md:text-2xl font-semibold mb-2">
                  This isn't another perk.
                </p>
                <p className="text-white/70 text-lg mb-6">
                  It's a <span className="text-red-400 font-bold">retention multiplier</span> — backed by real workforce data.
                </p>
                <Link to="/how-it-works">
                  <Button size="lg" className="btn-primary text-white">
                    See How It Works <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Retention Programs Section */}
      <section className="py-20 bg-white" data-testid="programs-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
              Retention{" "}
              <span className="gradient-text">Programs</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              CityWorth provides a structured approach to supporting employee homeownership with programs designed for every company.
            </p>
          </motion.div>

          {/* Program Cards - New Style */}
          <div className="max-w-5xl mx-auto space-y-6 mb-12">
            {/* Base Program - Full Width */}
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-6 border-2 border-blue-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                FOUNDATION
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-blue-800 text-xl mb-2">Base Retention</h4>
                  <p className="text-slate-600 text-sm mb-3">
                    The foundation program that introduces homeownership support and gives employees the tools and guidance needed to prepare for purchasing a home.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {["Homebuyer education", "Homebuying rewards", "Credit guidance", "Branded portal"].map((item, j) => (
                      <span key={j} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Premium & Executive Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Premium */}
              <div className="bg-white rounded-2xl p-6 border-2 border-green-400 relative overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  COMPANY-WIDE
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center flex-shrink-0">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-green-700 text-xl mb-2">Premium Retention</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Down payment assistance available for all full time employees who qualify.
                    </p>
                    <div className="text-green-600 font-semibold text-sm">
                      $5,000 – $15,000 typical
                    </div>
                  </div>
                </div>
              </div>

              {/* Executive */}
              <div className="bg-white rounded-2xl p-6 border-2 border-purple-400 relative overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  STRATEGIC
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center flex-shrink-0">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-purple-700 text-xl mb-2">Executive Retention</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Targeted down payment assistance used to retain key employees and leadership.
                    </p>
                    <div className="text-purple-600 font-semibold text-sm">
                      $15,000 – $50,000 typical
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link to="/retention-programs">
              <Button size="lg" className="btn-primary text-white">
                Explore All Programs
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-white" data-testid="how-it-works-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
              The Benefit That{" "}
              <span className="gradient-text">Creates Roots</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              CityWorth partners with forward-thinking organizations to deliver a Homeownership Employee Benefit — turning renters into owners and employees into long-term team members.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            
            className="bg-gradient-to-r from-red-600 to-blue-600 rounded-2xl p-8 md:p-12 text-white mb-16"
          >
            <h3 className="text-2xl font-bold mb-6 text-center">We Combine Everything Into One System</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              {["Real Estate", "Mortgage", "Insurance", "Education", "Employer Contributions"].map((item, i) => (
                <div key={i} className="bg-white/10 rounded-xl p-4">
                  <span className="font-medium">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-center mt-6 text-white/90">
              This removes confusion, reduces stress, and helps employees transition from renting to owning.
            </p>
          </motion.div>

          {/* Process Timeline */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 text-center mb-12">Simple 5-Step Process</h3>
            <div className="grid md:grid-cols-5 gap-4">
              {processSteps.map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  
                  transition={{ delay: i * 0.1 }}
                  className="relative"
                >
                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow text-center h-full">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-blue-500 text-white font-bold flex items-center justify-center mx-auto mb-4">
                      {step.num}
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">{step.title}</h4>
                    <p className="text-sm text-slate-600">{step.desc}</p>
                  </div>
                  {i < 4 && (
                    <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          <div className="text-center">
            {/* CTA - Goes to Get Started form */}
            <Link to="/get-started">
              <Button size="lg" className="btn-primary text-white">
                Schedule a Strategy Call
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Employee Retention Analysis Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-slate-900 to-slate-900 relative overflow-hidden" data-testid="retention-analysis-section">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-blue-500/20 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-red-500/10 via-transparent to-transparent" />
        
        <div className="container-main relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
                <BarChart3 className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-white">Data-Driven Insights</span>
              </div>

              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                Employee Retention{" "}
                <span className="text-blue-400">Analysis</span>
              </h2>
              
              <p className="text-xl text-white/80 mb-6">
                Discover exactly how homeownership impacts retention inside your company with our comprehensive data analysis.
              </p>

              <p className="text-lg text-white/60 mb-8">
                CityWorth combines workforce data, housing data, and employee sentiment to uncover patterns that help you understand how housing stability can strengthen your workforce.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: BarChart3, text: "Workforce retention study" },
                  { icon: Home, text: "Homeowner vs renter analysis" },
                  { icon: DollarSign, text: "Affordability insights" },
                  { icon: Users, text: "Employee sentiment data" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-white/80">
                    <item.icon className="w-5 h-5 text-blue-400" />
                    <span className="text-sm">{item.text}</span>
                  </div>
                ))}
              </div>

              <Link to="/retention-analysis">
                <Button size="lg" className="bg-blue-500 hover:bg-blue-400 text-white px-8 py-6 text-lg">
                  Learn About the Analysis
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              
              className="relative"
            >
              {/* Analytics visual card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <h3 className="text-xl font-bold text-white mb-6 text-center">What You'll Discover</h3>
                <div className="space-y-4">
                  {[
                    "How much longer homeowners stay vs renters",
                    "Retention patterns across your workforce",
                    "Employee homeownership opportunities",
                    "Housing affordability in your markets",
                    "Impact of employer contribution programs"
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      
                      transition={{ delay: i * 0.1 }}
                      className="flex items-center gap-3 bg-white/5 rounded-lg px-4 py-3"
                    >
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span className="text-white/90">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Floating stat badge */}
              <motion.div 
                className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-lg hidden lg:flex"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-slate-900">3x</div>
                    <div className="text-xs text-slate-500">Retention Boost</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Financial & Mental Wellness Section */}
      <section id="benefits" className="py-20 bg-slate-50" data-testid="wellness-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
              Stability Changes{" "}
              <span className="gradient-text">Everything</span>
            </h2>
          </motion.div>

          {/* Image banner - Sized similar to hero image with new homeowner badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            
            className="rounded-2xl overflow-hidden shadow-lg mb-12 relative max-w-4xl mx-auto"
          >
            <img 
              src={HAPPY_FAMILY} 
              alt="Happy family with their new home" 
              className="w-full h-[380px] object-cover"
            />
            <motion.div 
              className="absolute top-4 left-4 md:top-6 md:right-6 md:left-auto bg-white rounded-xl p-2 md:p-4 shadow-lg border border-slate-100"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Home className="w-4 h-4 md:w-6 md:h-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-sm md:text-lg font-bold text-slate-900">New Homeowner</div>
                  <div className="text-xs md:text-sm text-slate-500">Employee Benefit</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Financial Wellness */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              
              className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-green-500" />
                Financial Wellness
              </h3>
              <div className="space-y-4">
                {financialBenefits.map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{item.title}</h4>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Mental Wellness */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              
              className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Heart className="w-8 h-8 text-red-500" />
                Mental Wellness
              </h3>
              <div className="space-y-4">
                {mentalBenefits.map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{item.title}</h4>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Retention Data Section */}
      <section className="py-20 bg-white" data-testid="retention-section">
        <div className="container-main">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                The Retention{" "}
                <span className="gradient-text">Multiplier</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Internal analysis shows employees who own stay <strong>3x longer</strong> than renters.
              </p>
              <p className="text-lg text-slate-600 mb-6">
                Homeownership increases commitment.<br />
                Commitment strengthens culture.<br />
                Culture drives performance.
              </p>
              <p className="text-xl font-semibold text-slate-800 mb-8">
                When employees build roots, they build careers.
              </p>
              {/* CTA - Goes to Events page top */}
              <Link to="/events">
                <Button size="lg" className="btn-primary text-white">
                  Register for The Big Shift Webinar
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              
              className="space-y-6"
            >
              {/* Happy employees image */}
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={HAPPY_EMPLOYEES_2} 
                  alt="Happy team celebrating success" 
                  className="w-full h-48 object-cover"
                />
              </div>
              
              {/* Retention chart */}
              <div className="bg-slate-50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 text-center">Median Employee Tenure</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium text-slate-700">Renters</span>
                      <span className="text-slate-500">3.1 years</span>
                    </div>
                    <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full w-1/3 bg-slate-400 rounded-full" />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium text-slate-700">Homeowners</span>
                      <span className="text-green-600 font-semibold">9.3 years</span>
                    </div>
                    <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full w-full bg-gradient-to-r from-red-500 to-blue-500 rounded-full" />
                    </div>
                  </div>
                </div>
                <p className="text-center text-slate-500 mt-4 text-sm">Based on internal workforce analysis</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Christina's Story Section */}
      <section className="py-20 bg-white" data-testid="christina-story-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/177bfad2ced281f2f8c806981cf5eac35554ec14eae3130119705262109454a6.png" 
                alt="Christina in front of her new home" 
                className="w-full h-[350px] object-cover"
              />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-100 mb-6">
                <Heart className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-red-700">Real Employee Story</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                See How Christina{" "}
                <span className="gradient-text">Became a Homeowner</span>
              </h2>
              <p className="text-lg text-slate-600 mb-4">
                Christina had a good job and big goals, but homeownership felt out of reach. High prices, confusing loan options, and a complicated process kept her dream on hold.
              </p>
              <p className="text-lg text-slate-600 mb-6">
                Then her company introduced a homeownership benefit. With a dedicated team of real estate, mortgage, and insurance professionals working together for her, Christina went from <span className="font-semibold text-slate-800">feeling overwhelmed to owning her own home</span> — bringing less than $1,000 of her own money to closing.
              </p>
              <Link to="/employee-experience">
                <Button size="lg" className="btn-primary text-white px-8 py-6 text-lg">
                  Read Christina's Full Story
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Webinar Promotion Section - The Big Shift */}
      <section className="py-24 relative overflow-hidden" data-testid="webinar-section">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-blue-800" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-yellow-400/20 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-blue-400/20 via-transparent to-transparent" />
        
        {/* Floating shapes */}
        <motion.div 
          className="absolute top-10 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl"
          animate={{ y: [0, 30, 0], x: [0, 20, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-10 right-10 w-48 h-48 bg-yellow-400/10 rounded-full blur-2xl"
          animate={{ y: [0, -40, 0], x: [0, -30, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        
        <div className="container-main relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* Live badge with pulse */}
            <motion.div 
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-8"
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              <span className="text-white font-semibold tracking-wide">LIVE ONLINE EVENT</span>
            </motion.div>
            
            {/* Main headline */}
            <motion.h2 
              className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              
              transition={{ delay: 0.2 }}
            >
              The Big{" "}
              <span className="relative">
                <span className="relative z-10 text-yellow-400">Shift</span>
                <motion.span 
                  className="absolute -bottom-2 left-0 right-0 h-4 bg-yellow-400/30 -skew-x-12"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  
                  transition={{ delay: 0.5, duration: 0.4 }}
                />
              </span>
            </motion.h2>
            
            <motion.p 
              className="text-xl md:text-2xl text-white/90 mb-10 max-w-2xl mx-auto font-light"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              
              transition={{ delay: 0.3 }}
            >
              Why Homeownership Is Becoming the <span className="font-semibold text-yellow-300">Next Major Employee Benefit</span>
            </motion.p>
            
            {/* What you'll discover - white card */}
            <motion.div 
              className="bg-white rounded-3xl p-8 md:p-10 max-w-3xl mx-auto mb-10 shadow-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center justify-center gap-2">
                <Star className="w-6 h-6 text-yellow-500" />
                In This Webinar You'll Discover
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                {[
                  "Why retention strategies are failing",
                  "The true cost of turnover",
                  "The homeowner retention multiplier",
                  "Down payment program strategies",
                  "A new financial wellness model"
                ].map((item, i) => (
                  <motion.div 
                    key={i} 
                    className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    
                    transition={{ delay: 0.5 + i * 0.1 }}
                  >
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-800 font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              
              transition={{ delay: 0.6 }}
            >
              <a 
                href={ZOOM_WEBINAR_LINK} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg" 
                  className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-10 py-7 text-xl font-bold shadow-2xl shadow-yellow-400/30 hover:shadow-yellow-400/50 transition-all hover:scale-105"
                >
                  Reserve Your Free Spot Now
                  <ArrowRight className="ml-2 w-6 h-6" />
                </Button>
              </a>
              <p className="text-white/60 mt-4 text-sm">30 minutes • Live Q&A • 100% Free</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-white" data-testid="cta-section">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            
          >
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
              If Employees Stay Longer,{" "}
              <span className="gradient-text">Everything Changes</span>
            </h2>
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Let's explore what this could look like for your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {/* CTA - Goes to Get Started form */}
              <Link to="/get-started">
                <Button size="lg" className="btn-primary text-white px-8 py-6 text-lg">
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              {/* CTA - Goes to Events page top */}
              <Link to="/events">
                <Button size="lg" variant="outline" className="px-8 py-6 text-lg border-red-500 text-red-600 hover:bg-red-50">
                  Register for The Big Shift Webinar
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img src={LOGO_URL} alt="CityWorth" className="h-8" />
            <div className="flex items-center gap-6">
              <Link to="/careers" className="text-slate-600 hover:text-slate-900">Careers</Link>
              <Link to="/events" className="text-slate-600 hover:text-slate-900">Events</Link>
              <Link to="/get-started" className="text-slate-600 hover:text-slate-900">Contact</Link>
            </div>
            <p className="text-slate-500 text-sm">© {new Date().getFullYear()} CityWorth. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </motion.div>
  );
};

export default HomePage;
